import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { publicApi } from '@/lib/api';
import Navbar from '@/components/Navbar';
import HeroSection from '@/components/sections/HeroSection';
import AboutSection from '@/components/sections/AboutSection';
import ServicesSection from '@/components/sections/ServicesSection';
import PricingSection from '@/components/sections/PricingSection';
import PortfolioSection from '@/components/sections/PortfolioSection';
import ProcessSection from '@/components/sections/ProcessSection';
import TestimonialsSection from '@/components/sections/TestimonialsSection';
import ContactSection from '@/components/sections/ContactSection';
import Footer from '@/components/Footer';

// Helper to convert hex to HSL for CSS variables
const hexToHsl = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// Apply dynamic colors to CSS variables
const applyDynamicColors = (colors) => {
  if (!colors) return;
  
  const root = document.documentElement;
  
  if (colors.primary) {
    const hsl = hexToHsl(colors.primary);
    if (hsl) root.style.setProperty('--primary', hsl);
  }
  if (colors.secondary) {
    const hsl = hexToHsl(colors.secondary);
    if (hsl) root.style.setProperty('--secondary', hsl);
  }
  if (colors.accent) {
    const hsl = hexToHsl(colors.accent);
    if (hsl) root.style.setProperty('--accent', hsl);
  }
  if (colors.background) {
    const hsl = hexToHsl(colors.background);
    if (hsl) root.style.setProperty('--background', hsl);
  }
  if (colors.foreground) {
    const hsl = hexToHsl(colors.foreground);
    if (hsl) root.style.setProperty('--foreground', hsl);
  }
  if (colors.muted) {
    const hsl = hexToHsl(colors.muted);
    if (hsl) root.style.setProperty('--muted-foreground', hsl);
  }
  if (colors.card) {
    const hsl = hexToHsl(colors.card);
    if (hsl) root.style.setProperty('--card', hsl);
  }
  if (colors.border) {
    const hsl = hexToHsl(colors.border);
    if (hsl) {
      root.style.setProperty('--border', hsl);
      root.style.setProperty('--input', hsl);
    }
  }
};

const HomePage = () => {
  const [data, setData] = useState({
    hero: null,
    about: null,
    services: [],
    products: [],
    packages: [],
    portfolio: [],
    process: [],
    testimonials: [],
    settings: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // First seed data if needed
        await publicApi.seedData();
        
        // Then load all data
        const [hero, about, services, products, packages, portfolio, process, testimonials, settings] = await Promise.all([
          publicApi.getHero(),
          publicApi.getAbout(),
          publicApi.getServices(),
          publicApi.getProducts(),
          publicApi.getActivePackages(),
          publicApi.getActivePortfolio(),
          publicApi.getProcess(),
          publicApi.getActiveTestimonials(),
          publicApi.getSettings(),
        ]);

        // Apply dynamic colors
        if (settings.data?.colors) {
          applyDynamicColors(settings.data.colors);
        }

        setData({
          hero: hero.data,
          about: about.data,
          services: services.data,
          products: products.data,
          packages: packages.data,
          portfolio: portfolio.data,
          process: process.data,
          testimonials: testimonials.data,
          settings: settings.data,
        });
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="spinner mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <Navbar settings={data.settings} />
      <main>
        <HeroSection hero={data.hero} settings={data.settings} />
        <AboutSection about={data.about} />
        <ServicesSection services={data.services} products={data.products} />
        <PricingSection packages={data.packages} />
        <PortfolioSection portfolio={data.portfolio} />
        <ProcessSection process={data.process} />
        <TestimonialsSection testimonials={data.testimonials} />
        <ContactSection settings={data.settings} />
      </main>
      <Footer settings={data.settings} />
    </div>
  );
};

export default HomePage;
